import { Script } from 'gatsby';
import React, { useMemo, useState } from 'react';

import { generateCorrectSlugForPage } from '../../helpers/generate-correct-slug';
import { isBrowser } from '../../helpers/is-browser';
import { FourOFourPageContext } from '../../helpers/rendered-page-creation/create-404-pages';
import { createSeoSchema } from '../../helpers/seo-schema/seo-schema-helpers';
import { useGetLibraryDictionary } from '../../hooks/use-get-library-dictionary';
import {
  IGtmParameters,
  useSendGtmEvent,
} from '../../hooks/use-send-gtm-event';
import { IMeetingplacePageHome } from '../../queries/fragments/meetingPlacePageHome';
import {
  IMeetingplacePageHomeMinified,
  IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours,
} from '../../queries/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageLanding } from '../../queries/fragments/meetingPlacePageLanding';
import { PageContext } from '../../templates/page-context.interface';

interface SeoProps {
  title: string;
  description?: string;
  homepage?:
    | IMeetingplacePageHome
    | IMeetingplacePageHomeMinified
    | IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours
    | IMeetingPlacePageLanding;
  page?: any;
  imageUrl?: string;
  excludeBrowserTitleSuffix?: boolean;
  pageContext?: PageContext | FourOFourPageContext;
  indexPage?: boolean; // If false, the page will be excluded from the Elastic Search Index and from the SERP.
  schemaData?: any;
  category?: any;
}
export const Seo = ({
  title,
  homepage,
  description,
  page,
  imageUrl,
  excludeBrowserTitleSuffix,
  pageContext,
  indexPage = true,
  schemaData,
  category,
}: SeoProps) => {
  const iosAppId = process.env.IOS_APP_ID || process.env.GATSBY_IOS_APP_ID;
  const androidAppId =
    process.env.ANDROID_APP_ID || process.env.GATSBY_ANDROID_APP_ID;
  const domain = process.env.SITE_URL || process.env.GATSBY_SITE_URL;
  const language = pageContext?.language ? pageContext.language : 'en';

  let businessUnitCode = '';

  if (homepage && 'meetingPlace' in homepage) {
    const meetingPlace = homepage.meetingPlace;
    businessUnitCode = meetingPlace.businessUnitCode;
  }

  const dictionary = useGetLibraryDictionary(language);
  const pageCategory = pageContext?.parent?.name
    ? pageContext.parent.name
    : dictionary?.searchFacetOther;
  const oneTrustAccount = process.env.GATSBY_ONETRUST_ACCOUNT;
  description = description?.replace(/(\r\n|\n|\r)/gm, ' '); //Replace newlines with spaces

  const browserTitle = `${title} ${
    excludeBrowserTitleSuffix ? '' : pageContext?.htmlTitleSuffix
  }`;
  const [oneTrustLoaded, setOneTrustLoaded] = useState(false);

  let tagId = '';
  if (homepage && 'reserved__tagId' in homepage) {
    tagId = homepage.reserved__tagId?.replace('meetingPlace', '') ?? '';
  }

  const getGtmParams = () => {
    return {
      event: 'virtual_page_view',
      meeting_place: tagId,
      meeting_place_id: businessUnitCode ?? '',
      page_title: browserTitle,
      market: language,
      page_path: isBrowser && window.location.pathname,
    } as IGtmParameters;
  };

  const gtmParamsCached = useMemo(getGtmParams, [
    browserTitle,
    businessUnitCode,
    language,
    tagId,
  ]);
  useSendGtmEvent(gtmParamsCached);

  // Create structured data(rich results) for SEO
  const structuredSeoData = schemaData && createSeoSchema(schemaData);

  const categoryNames = category?.map((obj: any) => obj.name);

  return (
    <>
      <html className="h-full" lang={language} />
      <title>{browserTitle}</title>
      <body className="h-full" />
      <link rel="icon" type="image/png" href={homepage?.favicon.url}></link>
      <link rel="preconnect" href="https://cdn.cookielaw.org" />
      {/* Remove following row after deploy if assets should be fetched from akamai. */}
      <link rel="preconnect" href="https://images.ctfassets.net" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover"
      />
      {!indexPage && <meta name="robots" content="noindex" />}
      {description && <meta name="description" content={description} />}
      {businessUnitCode && (
        <meta name="meetingplaceid" content={businessUnitCode} />
      )}

      {/*
      ELASTIC APP SEARCH
      */}
      <meta className="elastic" name="search_title" content={title} />
      <meta className="elastic" name="node_locale" content={language} />
      <meta className="elastic" name="page_category" content={pageCategory} />
      <meta className="elastic" name="category" content={categoryNames} />
      {imageUrl && (
        <meta className="elastic" name="page_image" content={imageUrl} />
      )}

      {/*
      OPEN GRAPH
      */}
      <meta name="og:type" content="website" />
      <meta name="og:title" content={title} />
      {imageUrl && <meta name="og:image" content={imageUrl} />}
      {description && <meta name="og:description" content={description} />}
      {page && (
        <meta
          name="og:url"
          content={domain + generateCorrectSlugForPage(page)}
        />
      )}

      {/* ios App promotion widget/smart app banner */}
      {iosAppId && (
        <meta name="apple-itunes-app" content={`app-id=${iosAppId}`} />
      )}

      {/* Android App promotion widget.smart app banner */}
      {androidAppId && (
        <meta name="google-play-app" content={`app-id=${androidAppId}`} />
      )}

      {/* SEO Schema Markup */}
      {structuredSeoData && (
        <script type="application/ld+json" id="structured-data">
          {JSON.stringify(structuredSeoData)}
        </script>
      )}

      {/* script to set default consents */}
      <Script>
        {`
        window.dataLayer = window.dataLayer || []; 
        function gtag(){dataLayer.push(arguments);}
        gtag('set', 'developer_id.dNzMyY2', true);
        gtag('consent', 'default', {'analytics_storage': 'denied'}); gtag('consent', 'default', {'ad_storage': 'denied'});`}
      </Script>

      {/*
      ONETRUST
      */}
      <Script
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={oneTrustAccount}
        data-document-language="true"
        onLoad={() => setOneTrustLoaded(true)}
      ></Script>
      {oneTrustLoaded && (
        <Script>
          {`
          function OptanonWrapper() { 
            if (OnetrustActiveGroups.indexOf('C0002') != -1) 
            { gtag('consent', 'update', {'analytics_storage': 'granted'})}; 
            if (OnetrustActiveGroups.indexOf('C0004') != -1) 
            { gtag('consent', 'update', {'ad_storage': 'granted'})};}`}
        </Script>
      )}

      {/*
      CANONICAL LINK
      Renders an absolute link to the current page.
      */}
      {pageContext?.canonicalLink && (
        <link rel="canonical" href={domain + pageContext?.canonicalLink} />
      )}

      {/*
      ALTERNATE LINKS (Only render if there is more than one language).
      Renders absolute links to the current page in all available languages.
      */}
      {pageContext?.hrefLangTags &&
        pageContext?.hrefLangTags?.length > 1 &&
        pageContext?.hrefLangTags.map((hrefLangTag) => (
          <link
            key={hrefLangTag.hreflang}
            rel="alternate"
            href={domain + hrefLangTag.href}
            hrefLang={hrefLangTag.hreflang}
          />
        ))}
    </>
  );
};
